import ButtonContainer from 'app/shared-components/button-container/button-container';
import * as yup from 'yup';
import { Role } from 'API';
import { addUser, getAllUsersNoLimit, selectUsersSlice } from 'app/pages/store/usersSlice';
import { formatEmail } from 'util/sanitizeText';
import { userService } from 'app/services/user.service';
import { yupResolver } from '@hookform/resolvers/yup';
import { isErrorLike } from 'util/serializeError';
import { useTranslation } from 'react-i18next';
import { DEFAULT_USER_FILTER } from 'app/store/filtering/userFilterConfig';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUpdateFirstRunState } from 'app/hooks/useUpdateFirstRunState';
import { useUserRole } from 'app/hooks/useUserRole';
import { useAppDispatch, useAppSelector } from 'app/store';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Theme,
  useTheme,
} from '@mui/material';

/**
 * Form Validation Schema
 **/
const schema = yup.object({
  email: yup.string().required('You must enter a email').email('You must enter a valid email'),
  roles: yup.string().required(),
});

type FormValues = yup.InferType<typeof schema>;
interface AddUserProps {
  sx?: SxProps<Theme>;
  onClose?: () => void;
  onUserAdded?: () => void;
  isStepperForm?: boolean;
  renderButtons: (formProps: {
    isLoading: boolean;
    isValid: boolean;
    addPaidolUser: () => void;
  }) => React.ReactNode;
}

function AddUserForm({ onClose, onUserAdded, renderButtons, isStepperForm = false, sx }: AddUserProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // SEE-946: filter the role dropdown by the inviter's invite capabilities.
  // Secondary cannot invite Primary; Approval/Cardholder cannot invite anyone
  // (the AddUserForm action bar is already hidden in those cases).
  const {
    additionalAdminRolesEnabled,
    canInvitePrimaryAdmin,
    canInviteSecondaryAdmin,
    canInviteApprovalAdmin,
    canInviteGroupAdmin,
    canInviteCardholder,
  } = useUserRole();

  const roleOptions = useMemo(() => {
    // SEE-946: ordered by access hierarchy (most → least). Keeps the dropdown
    // intuitive regardless of which options the current inviter can see.
    const options: { value: Role; label: string }[] = [];
    if (canInvitePrimaryAdmin) options.push({ value: Role.ADMINISTRATOR, label: t('administrator') });
    if (additionalAdminRolesEnabled && canInviteSecondaryAdmin) {
      options.push({ value: Role.SECONDARY_ADMINISTRATOR, label: t('secondaryAdmin') });
    }
    if (additionalAdminRolesEnabled && canInviteApprovalAdmin) {
      options.push({ value: Role.APPROVAL_ADMINISTRATOR, label: t('approvalAdmin') });
    }
    if (canInviteGroupAdmin) options.push({ value: Role.GROUP_ADMINISTRATOR, label: t('groupAdmin') });
    if (canInviteCardholder) options.push({ value: Role.CARDHOLDER, label: t('cardholder') });
    return options;
  }, [
    additionalAdminRolesEnabled,
    canInviteGroupAdmin,
    canInvitePrimaryAdmin,
    canInviteSecondaryAdmin,
    canInviteApprovalAdmin,
    canInviteCardholder,
    t,
  ]);

  // Default to ADMINISTRATOR for stepper (initial paidol setup), else CARDHOLDER.
  // Fall back to the first available option if the inviter lacks the default.
  const defaultRole = useMemo(() => {
    const preferred = isStepperForm ? Role.ADMINISTRATOR : Role.CARDHOLDER;
    if (roleOptions.some((o) => o.value === preferred)) return preferred;
    return roleOptions[0]?.value ?? preferred;
  }, [isStepperForm, roleOptions]);

  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      email: '',
      roles: defaultRole,
    },
    resolver: yupResolver(schema),
  });
  const { control, formState, getValues, setError } = methods;
  const { errors, isValid } = formState;
  const [isLoading, setIsLoading] = useState(false);

  const { selectedPaidolId: selectedCompany } = useAppSelector(selectUserCompanies);
  // SEE-946: catch the "user already in this paidol" case at the form layer
  // so we can show a clean inline message. The CDK backend dispatcher
  // collapses all handler exceptions into a single generic
  // "An unexpected error occurred" string (request.py:GENERIC_GRAPHQL_ERROR_MESSAGE)
  // — intentional so internals don't leak, but it means the frontend can't
  // tell duplicate-invite from any other failure post-fact. So we do a
  // pre-check against the loaded users list. Only matches the *current*
  // paidol; users that exist on another paidol fall through to the normal
  // invite flow which adds the paidol switcher entry server-side.
  const { allUsers: existingPaidolUsers } = useAppSelector(selectUsersSlice);

  // The Team page populates `allUsers` via retrieveUsersManagementFilterOptions
  // on mount, but the invite form can open before that thunk completes
  // (race) OR from contexts other than the Team page (stepper, first-run
  // checklist) where it isn't populated at all. Trigger the load here so
  // the pre-check below always has fresh data. The thunk is idempotent
  // server-side; if the Team page already loaded the same list, this is
  // just a refresh.
  useEffect(() => {
    if (selectedCompany) {
      dispatch(getAllUsersNoLimit({ selectedCompany }));
    }
  }, [dispatch, selectedCompany]);

  const { triggerUpdate } = useUpdateFirstRunState({
    operationName: 'completeFirstRunChecklist',
    firstRunValue: getValues().roles === Role.ADMINISTRATOR ? 'addAdmin' : 'addTeamMember',
  });

  const addPaidolUser = useCallback(async () => {
    const { email } = getValues();
    const normalizedEmail = (email || '').trim().toLowerCase();

    setIsLoading(true);

    // Re-check at submit time against an authoritative server response so
    // a fast-typer can't beat the mount-time slice population, and so we
    // catch users added/removed since the form mounted. Call the service
    // directly (rather than dispatching getAllUsersNoLimit) so we don't
    // overwrite the shared `allUsers` slice with a 1-row filtered result
    // that the Team page user table also depends on.
    //
    // The DEFAULT_USER_FILTER opts both registered AND pending (invited
    // but not yet activated) users into the response — without it, the
    // backend's default excludes user_id=null rows, so a pending invite
    // for the same email would slip past the pre-check. Scoped by
    // paidol_id to *this* paidol only, so a user invited in another
    // paidol falls through to the normal invite flow (which adds the
    // paidol switcher entry server-side).
    let serverHit = false;
    try {
      const response = await userService.listPaidolUsersFiltered({
        paidol_id: selectedCompany,
        email: normalizedEmail,
        filter: DEFAULT_USER_FILTER,
        limit: 5,
      });
      const items = (response?.items ?? []).filter(Boolean) as typeof existingPaidolUsers;
      if (items.some((u) => u?.email?.toLowerCase() === normalizedEmail && !u?.deletedAt)) {
        setIsLoading(false);
        setError('email', { type: 'manual', message: t('addTeamMember.alreadyInPaidol') });
        return 'rejected';
      }
      serverHit = true;
    } catch {
      // Network/auth failure on the pre-check shouldn't block the invite —
      // fall through to the slice-based check, then to the backend (which
      // returns the generic GraphQL error). Better to occasionally show the
      // generic error than to block legitimate invites on a flaky lookup.
    }

    if (!serverHit) {
      const alreadyInPaidol = existingPaidolUsers.some(
        (u) => u?.email?.toLowerCase() === normalizedEmail && !u?.deletedAt
      );
      if (alreadyInPaidol) {
        setIsLoading(false);
        setError('email', { type: 'manual', message: t('addTeamMember.alreadyInPaidol') });
        return 'rejected';
      }
    }

    return dispatch(addUser({ ...getValues(), selectedCompany })).then(async (res) => {
      setIsLoading(false);
      await triggerUpdate();

      if (isErrorLike(res?.payload)) {
        setError('email', { type: 'manual', message: res?.payload?.message });
      } else if (onClose) {
        enqueueSnackbar('User added successfully', { variant: 'success' });
        // Refresh users and filters before closing
        if (onUserAdded) {
          onUserAdded();
        }
        onClose();
      }
      return res.meta.requestStatus; // Ensures a value is always returned
    });
  }, [
    dispatch,
    existingPaidolUsers,
    getValues,
    onClose,
    onUserAdded,
    selectedCompany,
    setError,
    enqueueSnackbar,
    t,
    triggerUpdate,
  ]);

  return (
    <FormProvider {...methods}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: '20px',
          border: '2px solid',
          borderColor: theme.palette.primary.lighter,
          borderRadius: '16px',
          minWidth: '238px',
          width: '100%',
          maxWidth: '376px',
          ...sx,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="email"
                error={!!errors.email}
                helperText={errors?.email?.message}
                label="Email"
                id="email"
                variant="outlined"
                required
                fullWidth
                onBlur={(e) => field.onChange(formatEmail(e.target.value))}
              />
            )}
          />
        </Box>
        <Controller
          name="roles"
          control={control}
          render={({ field }) => (
            <FormControl>
              <InputLabel id="role-label">{t('role')}</InputLabel>
              <Select {...field} id="roles" labelId="role-label" label="Role" fullWidth>
                {roleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Box>
      <ButtonContainer sx={{ ...sx }}>{renderButtons({ isLoading, isValid, addPaidolUser })}</ButtonContainer>
    </FormProvider>
  );
}

export default AddUserForm;
